import { BLOCKS } from '@contentful/rich-text-types';

import type { RichContentType } from '../components/RichContent';
import type { Block, Node, Text, TopLevelBlock } from '@contentful/rich-text-types';
import type React from 'react';

type DisplayName = { displayName?: 'RCParagraph' | 'RCHeading' } | undefined;

const RCHeading: React.FC<{ children: React.ReactNode }> & DisplayName = ({ children }) => <>{children}</>;
RCHeading.displayName = 'RCHeading';

const RCParagraph: React.FC<{ children: React.ReactNode }> & DisplayName = ({ children }) => <>{children}</>;
RCParagraph.displayName = 'RCParagraph';

type Elements = React.ReactElement & { type: DisplayName };

const parseElementRecursively = (element: Elements | Elements[]): Node | Node[] => {
  if (typeof element === 'string') {
    return {
      data: {},
      marks: [],
      nodeType: 'text',
      value: element,
    } as Text;
  }

  if (Array.isArray(element)) {
    return element.map(parseElementRecursively as (el: React.ReactElement) => TopLevelBlock);
  }

  if (typeof element.type === 'string') {
    // Just for type safety. Not really necessary logically, as the last line of the function does the same.
    return parseElementRecursively(element.props.children);
  }

  if (element.type.displayName === 'RCHeading') {
    return {
      content: [parseElementRecursively(element.props.children as React.ReactElement)].flat(),
      data: {},
      nodeType: BLOCKS.HEADING_2,
    } as Block;
  }

  if (element.type.displayName === 'RCParagraph') {
    return {
      content: [parseElementRecursively(element.props.children as React.ReactElement)].flat(),
      data: {},
      nodeType: BLOCKS.PARAGRAPH,
    } as Block;
  }

  return parseElementRecursively(element.props.children);
};

/**
 * How to use:
 * <RichContent
 *  content={createRichContent(
 *    <>
 *      <RCHeadiing>Title</RCHeading>
 *      <RCParagraph>Content</RcParagraph>
 *    </>
 *  )}
 * />
 *
 * For now, only Fragment, RCHeading and RCParagraph are supported as the element arg
 */
const createRichContent = (element: React.ReactElement | React.ReactElement[]): RichContentType => {
  const content = parseElementRecursively(element) as TopLevelBlock | TopLevelBlock[];

  return {
    content: {
      json: {
        content: Array.isArray(content) ? content : [content],
        data: {},
        nodeType: BLOCKS.DOCUMENT,
      },
    },
  };
};

export { RCHeading, RCParagraph, createRichContent };
